import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import labourService from '../../services/labour';
import fieldWorkService from '../../services/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import { dateTimeLocalToISO, formatDateTimeForInput, formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { workerDisplayName, type Worker } from '../../types/fieldWork';
import type { CostCentre, PlaceTarget, WorkSegment } from '../../types/labour';
import { DateRangeInputs, Notice } from './shared';
import { useDateRange, usePlaceOptions } from './labourUtils';

const SOURCE_LABEL: Record<string, string> = { crew: 'Crew mode', self: 'Own job', manual: 'Entered', gps: 'GPS' };

function placeLabel(s: WorkSegment): string {
  if (s.cost_centre_name) return s.cost_centre_name;
  return [s.block_name, s.cultivar_area_name].filter(Boolean).join(' › ') || s.farm_name || 'Job area (shared)';
}

/**
 * Every time record in a range: who, where, how long, and how it was
 * recorded. Add time nobody recorded, correct a record (a reason is required
 * and kept), or void one entered in error — voided time leaves every total.
 */
export default function TimeRecordsPanel() {
  const tz = useOrgTimezone();
  const range = useDateRange();
  const [segments, setSegments] = useState<WorkSegment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<WorkSegment | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = dateTimeLocalToISO(`${range.from}T00:00`, tz);
      const to = new Date(new Date(dateTimeLocalToISO(`${range.to}T00:00`, tz)).getTime() + 86_400_000).toISOString();
      setSegments(await labourService.getSegments({ from, to }));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load time records'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, tz]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRangeInputs {...range} />
        <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add time
        </Button>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {loading ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Loading…</p>
      ) : segments.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No time recorded in this range.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Time records">
          {segments.map(s => (
            <li key={s.id}>
              <button type="button" onClick={() => setEditing(s)} className="w-full text-left px-3 py-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium text-neutral-900">{workerDisplayName(s)}</span>
                  <span className="text-sm text-neutral-700">{Number(s.hours).toFixed(2)} h</span>
                  {!s.ended_at && <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">On now</span>}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{SOURCE_LABEL[s.source] ?? s.source}</span>
                  {s.edit_reason && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700" title={s.edit_reason}>Edited</span>}
                </div>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {formatDateTimeInTz(s.started_at, tz)}{s.ended_at ? ` – ${formatDateTimeInTz(s.ended_at, tz)}` : ''}
                  {' · '}{placeLabel(s)}
                  {s.job_name && ` · ${s.job_name}`}
                  {s.activity_type_name && ` · ${s.activity_type_name}`}
                  {s.units_done != null && ` · ${Number(s.units_done)} ${s.unit ?? ''}`}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <SegmentModal
          segment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SegmentModal({ segment, onClose, onSaved }: { segment: WorkSegment | null; onClose: () => void; onSaved: () => void }) {
  const tz = useOrgTimezone();
  const places = usePlaceOptions();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [workerId, setWorkerId] = useState<number | ''>(segment?.worker_id ?? '');
  const initialPlace = segment?.cost_centre_id ? `cc:${segment.cost_centre_id}`
    : segment?.cultivar_area_id ? `cultivar_area:${segment.cultivar_area_id}`
      : segment?.block_id ? `block:${segment.block_id}` : '';
  const [placeKey, setPlaceKey] = useState(initialPlace);
  const [start, setStart] = useState(segment ? formatDateTimeForInput(segment.started_at, tz) : '');
  const [end, setEnd] = useState(segment?.ended_at ? formatDateTimeForInput(segment.ended_at, tz) : '');
  const [breakMin, setBreakMin] = useState(String(segment?.break_minutes ?? 0));
  const [units, setUnits] = useState(segment?.units_done != null ? String(Number(segment.units_done)) : '');
  const [unit, setUnit] = useState(segment?.unit ?? '');
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fieldWorkService.getWorkers().then(setWorkers).catch(() => setWorkers([]));
    labourService.getCostCentres().then(setCostCentres).catch(() => setCostCentres([]));
  }, []);

  const placePayload = (): { target: PlaceTarget | null; cost_centre_id: number | null } => {
    if (placeKey.startsWith('cc:')) return { target: null, cost_centre_id: Number(placeKey.slice(3)) };
    const p = places.find(x => x.key === placeKey);
    return { target: p ? p.target : null, cost_centre_id: null };
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (voiding && segment) {
        await labourService.voidSegment(segment.id, reason);
      } else if (segment) {
        await labourService.updateSegment(segment.id, {
          started_at: dateTimeLocalToISO(start, tz),
          ended_at: end ? dateTimeLocalToISO(end, tz) : null,
          break_minutes: Number(breakMin) || 0,
          units_done: units === '' ? null : Number(units),
          unit: unit || null,
          ...(placeKey !== initialPlace ? placePayload() : {}),
          reason,
        });
      } else {
        await labourService.createSegment({
          worker_id: Number(workerId),
          started_at: dateTimeLocalToISO(start, tz),
          ended_at: dateTimeLocalToISO(end, tz),
          break_minutes: Number(breakMin) || 0,
          units_done: units === '' ? null : Number(units),
          unit: unit || null,
          ...placePayload(),
        });
      }
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const valid = voiding
    ? reason.trim().length >= 3
    : !!start && (segment ? reason.trim().length >= 3 : !!workerId && !!end && !!placeKey);

  const groups = [...new Set(places.map(p => p.group))];

  return (
    <FormModal
      title={voiding ? 'Void time record' : segment ? 'Correct time record' : 'Add time'}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={voiding ? 'Void' : 'Save'}
      submitting={saving}
      submitDisabled={!valid}
      error={error}
    >
      {!voiding && (
        <>
          {segment ? (
            <p className="text-sm text-neutral-700 font-medium">{workerDisplayName(segment)}</p>
          ) : (
            <Field label="Person" htmlFor="seg-worker">
              <select id="seg-worker" className={inputClass} value={workerId} onChange={e => setWorkerId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Choose…</option>
                {workers.map(w => <option key={w.id} value={w.id}>{workerDisplayName(w)} ({w.picker_code})</option>)}
              </select>
            </Field>
          )}
          <Field label="Where" htmlFor="seg-place">
            <select id="seg-place" className={inputClass} value={placeKey} onChange={e => setPlaceKey(e.target.value)}>
              {segment && <option value={initialPlace}>{placeLabel(segment)}</option>}
              {!segment && <option value="">Choose…</option>}
              {groups.map(g => (
                <optgroup key={g} label={g}>
                  {places.filter(p => p.group === g && p.key !== initialPlace).map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </optgroup>
              ))}
              {costCentres.length > 0 && (
                <optgroup label="Not in a block">
                  {costCentres.filter(c => `cc:${c.id}` !== initialPlace).map(c => <option key={c.id} value={`cc:${c.id}`}>{c.name}</option>)}
                </optgroup>
              )}
            </select>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Start" htmlFor="seg-start">
              <input id="seg-start" type="datetime-local" className={inputClass} value={start} onChange={e => setStart(e.target.value)} />
            </Field>
            <Field label="Finish" htmlFor="seg-end" hint={segment && !segment.ended_at ? 'Leave empty while they are still on.' : undefined}>
              <input id="seg-end" type="datetime-local" className={inputClass} value={end} onChange={e => setEnd(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Break (min)" htmlFor="seg-break">
              <input id="seg-break" type="number" inputMode="numeric" min={0} className={inputClass} value={breakMin} onChange={e => setBreakMin(e.target.value)} />
            </Field>
            <Field label="Done" htmlFor="seg-units">
              <input id="seg-units" type="number" inputMode="decimal" min={0} className={inputClass} value={units} onChange={e => setUnits(e.target.value)} />
            </Field>
            <Field label="Unit" htmlFor="seg-unit">
              <select id="seg-unit" className={inputClass} value={unit} onChange={e => setUnit(e.target.value)}>
                <option value="">—</option>
                {['trees', 'rows', 'bins', 'kg', 'tonnes', 'hectares'].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>
        </>
      )}
      {(segment || voiding) && (
        <Field label={voiding ? 'Why is this being voided?' : 'Reason for the change'} htmlFor="seg-reason">
          <input id="seg-reason" className={inputClass} value={reason} onChange={e => setReason(e.target.value)}
            placeholder={voiding ? 'e.g. entered on the wrong person' : 'e.g. smoko not recorded'} />
        </Field>
      )}
      {segment && (
        <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => setVoiding(v => !v)}>
          {voiding ? 'Back to editing' : 'Void this record instead'}
        </button>
      )}
    </FormModal>
  );
}
